import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { LuMessageSquare } from "react-icons/lu";
import { Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { getUsageSummary, resolveDisplayNames } from "../../utils/services/api";
import RecentQueryLogTable from "./RecentQueryLogTable";

// Chart chrome (muted grid/axis ink) — keeps the data ink, not the
// scaffolding, doing the talking.
const GRIDLINE = "#e1e0d9";
const AXIS_LINE = "#c3c2b7";
const MUTED_TEXT = "#898781";
const AXIS_TICK = { fontSize: 12, fill: MUTED_TEXT };
const CHART_HEIGHT = 384; // matches the h-96 container below

// Full email addresses need real room at a 45° angle — give each category a
// wide, fixed slot and let the chart scroll horizontally (see the
// overflow-x-auto container below) rather than truncate any name.
const userChartWidth = (categoryCount) => Math.max(600, categoryCount * 110);

// Deliberately not using Recharts' ResponsiveContainer here: nesting a
// percentage-width chart inside a fixed-width/min-width scroll wrapper (the
// only way to get "fill available space normally, but a wide scrollable
// chart once there are many users") raced with its ResizeObserver-based
// measurement — bars would sometimes render with zero size on first paint
// and only reappear after some unrelated re-render nudged a remeasure.
// Measuring the container ourselves and passing BarChart a plain numeric
// width/height removes that race entirely.
//
// Uses a CALLBACK ref (not useRef) — the chart's div only exists once the
// loading/empty conditional above it resolves, so a plain useRef effect
// (which only runs once, on this component's own mount) measures a null
// node and permanently locks in width=0. A callback ref re-fires the moment
// that div actually mounts, and ResizeObserver keeps it accurate afterward
// (layout shifts, sidebar toggle, etc.), not just on window resize.
const useContainerWidth = () => {
  const [node, setNode] = useState(null);
  const [width, setWidth] = useState(0);
  const ref = useCallback((el) => setNode(el), []);
  useEffect(() => {
    if (!node) return;
    const measure = () => setWidth(node.offsetWidth || 0);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [ref, width];
};

// Rolling windows ending now — kept in one place since both the "My usage"
// and admin usage-by-user filters share the same options/labels.
const USAGE_RANGE_OPTIONS = [
  { value: "yesterday", label: "Yesterday" },
  { value: "week", label: "Last week" },
  { value: "month", label: "Last month" },
  { value: "all", label: "Overall" },
];

const RangeSelect = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="text-xs border border-[#EEEBEB] rounded-md px-2 py-1 text-[#3A3A3A] bg-white focus:outline-none focus:ring-1 focus:ring-[#00AEEF]"
  >
    {USAGE_RANGE_OPTIONS.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </select>
);

const ActivityLog = () => {
  const currentUserId = (localStorage.getItem("userId") || "").toLowerCase();

  const [myUsage, setMyUsage] = useState([]);
  const [loadingMyUsage, setLoadingMyUsage] = useState(true);
  const [myUsageRange, setMyUsageRange] = useState("all");
  const [allUsage, setAllUsage] = useState([]);
  const [loadingAllUsage, setLoadingAllUsage] = useState(true);
  const [allUsageRange, setAllUsageRange] = useState("all");
  // email -> real Entra display name, for showing real names instead of raw
  // emails/"unknown" on the by-user charts and question list.
  const [userNames, setUserNames] = useState({});

  const [usageChartRef, usageContainerWidth] = useContainerWidth();
  const [queriesChartRef, queriesContainerWidth] = useContainerWidth();

  const labelForUser = (userId) => {
    if (!userId || userId === "unknown") return "Unknown user (legacy)";
    return userNames[userId] || userId;
  };

  const loadMyUsage = async (range = myUsageRange) => {
    if (!currentUserId) {
      setLoadingMyUsage(false);
      return;
    }
    try {
      setLoadingMyUsage(true);
      setMyUsage(await getUsageSummary(currentUserId, range));
    } catch (error) {
      toast.error("Failed to load your usage");
    } finally {
      setLoadingMyUsage(false);
    }
  };

  const loadAllUsage = async (range = allUsageRange) => {
    try {
      setLoadingAllUsage(true);
      setAllUsage(await getUsageSummary(undefined, range));
    } catch (error) {
      toast.error("Failed to load usage overview");
    } finally {
      setLoadingAllUsage(false);
    }
  };

  // Fires on mount too (range starts at "all"), so this alone covers both
  // the initial load and subsequent range changes — no separate mount effect needed.
  useEffect(() => {
    loadMyUsage(myUsageRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUsageRange]);

  useEffect(() => {
    loadAllUsage(allUsageRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsageRange]);

  // Resolve real display names for every user_id that shows up in the usage
  // charts, once we know what they are — batched into one call rather than
  // one per row. RecentQueryLogTable resolves names for its own (paginated,
  // independently-loaded) rows itself.
  useEffect(() => {
    const emails = new Set();
    allUsage.forEach((row) => {
      if (row.user_id && row.user_id !== "unknown") emails.add(row.user_id);
    });
    if (emails.size === 0) return;
    resolveDisplayNames(Array.from(emails)).then((names) => {
      setUserNames((prev) => ({ ...prev, ...names }));
    });
  }, [allUsage]);

  const myTotalTokens = useMemo(() => myUsage.reduce((sum, row) => sum + (row.total_tokens || 0), 0), [myUsage]);
  const myTotalQueries = useMemo(() => myUsage.reduce((sum, row) => sum + (row.query_count || 0), 0), [myUsage]);

  const usageByUser = useMemo(() => {
    const totals = {};
    allUsage.forEach((row) => {
      if (row.user_id === "unknown") return; // legacy data with no real attribution — excluded per request
      totals[row.user_id] = (totals[row.user_id] || 0) + (row.total_tokens || 0);
    });
    return Object.entries(totals)
      .map(([user_id, total_tokens]) => ({ user_id, label: labelForUser(user_id), total_tokens }))
      .sort((a, b) => b.total_tokens - a.total_tokens)
      .slice(0, 25);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsage, userNames]);

  const queriesByUser = useMemo(() => {
    const totals = {};
    allUsage.forEach((row) => {
      if (row.user_id === "unknown") return; // legacy data with no real attribution — excluded per request
      totals[row.user_id] = (totals[row.user_id] || 0) + (row.query_count || 0);
    });
    return Object.entries(totals)
      .map(([user_id, query_count]) => ({ user_id, label: labelForUser(user_id), query_count }))
      .sort((a, b) => b.query_count - a.query_count)
      .slice(0, 25);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsage, userNames]);

  return (
    <div className="flex flex-col gap-6 h-full w-full bg-slate-100 px-4 py-6 overflow-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[#3A3A3A]">Activity Log</h1>
        <Link
          to="/chat?new_conversation"
          className="text-[#00AEEF] text-sm flex items-center gap-2 font-semibold px-3 py-2 hover:bg-[#00AEEF] hover:text-white bg-white rounded-[4px] border border-[#00AEEF] transition-colors duration-300 ease-in-out"
        >
          <LuMessageSquare size={16} />
          Chat
        </Link>
      </div>

      {/* My usage section */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-[#3A3A3A]">My usage</h2>
          <RangeSelect value={myUsageRange} onChange={setMyUsageRange} />
        </div>
        {loadingMyUsage ? (
          <p className="text-sm text-[#616161]">Loading…</p>
        ) : myUsage.length === 0 ? (
          <p className="text-sm text-[#616161]">
            No usage recorded yet for {currentUserId || "your account"} — this fills in as you ask questions.
          </p>
        ) : (
          // Per-model breakdown retired along with the rest of Phase-3 — even
          // "Overall" range history spanning the old multi-model comparison
          // period is rolled into these two totals rather than split back out
          // by model, since that split is no longer a meaningful dimension.
          <div className="flex items-center gap-10 md:gap-16 py-2">
            <div>
              <p className="text-4xl font-bold text-[#3A3A3A]">{myTotalTokens.toLocaleString()}</p>
              <p className="text-xs text-[#616161] mt-1">Total tokens consumed</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#3A3A3A]">{myTotalQueries.toLocaleString()}</p>
              <p className="text-xs text-[#616161] mt-1">Total queries asked</p>
            </div>
          </div>
        )}
      </div>

      {/* All-up usage overview, per user */}
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-[#3A3A3A]">Usage overview (all users)</h2>
        <RangeSelect value={allUsageRange} onChange={setAllUsageRange} />
      </div>
      <div className="flex flex-col gap-6">
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
          <h2 className="font-semibold text-[#3A3A3A] mb-3">Usage by user (top 25)</h2>
          {loadingAllUsage ? (
            <p className="text-sm text-[#616161]">Loading…</p>
          ) : usageByUser.length === 0 ? (
            <p className="text-sm text-[#616161]">No usage recorded yet across any user.</p>
          ) : (
            <div ref={usageChartRef} className="w-full h-96 overflow-x-auto">
              <BarChart
                width={Math.max(userChartWidth(usageByUser.length), usageContainerWidth)}
                height={CHART_HEIGHT}
                data={usageByUser}
                margin={{ top: 8, right: 16, bottom: 70, left: 8 }}
                barCategoryGap="20%"
              >
                <CartesianGrid vertical={false} stroke={GRIDLINE} />
                <XAxis
                  dataKey="label"
                  tick={{ ...AXIS_TICK, fontSize: 11 }}
                  axisLine={{ stroke: AXIS_LINE }}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => v.toLocaleString()} />
                <Tooltip formatter={(value) => [value.toLocaleString(), "Total tokens"]} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Bar dataKey="total_tokens" fill="#2a78d6" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </div>
          )}
        </div>

        {/* Number of queries per user */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
          <h2 className="font-semibold text-[#3A3A3A] mb-3">Queries by user (top 25)</h2>
          {loadingAllUsage ? (
            <p className="text-sm text-[#616161]">Loading…</p>
          ) : queriesByUser.length === 0 ? (
            <p className="text-sm text-[#616161]">No queries recorded yet across any user.</p>
          ) : (
            <div ref={queriesChartRef} className="w-full h-96 overflow-x-auto">
              <BarChart
                width={Math.max(userChartWidth(queriesByUser.length), queriesContainerWidth)}
                height={CHART_HEIGHT}
                data={queriesByUser}
                margin={{ top: 8, right: 16, bottom: 70, left: 8 }}
                barCategoryGap="20%"
              >
                <CartesianGrid vertical={false} stroke={GRIDLINE} />
                <XAxis
                  dataKey="label"
                  tick={{ ...AXIS_TICK, fontSize: 11 }}
                  axisLine={{ stroke: AXIS_LINE }}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip formatter={(value) => [value.toLocaleString(), "Queries"]} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Bar dataKey="query_count" fill="#008300" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </div>
          )}
        </div>
      </div>

      {/* Recent Query Log */}
      <RecentQueryLogTable />
    </div>
  );
};

export default ActivityLog;
